import React, { useMemo, useState } from "react";
import {
  Stack,
  Typography,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Autocomplete,
  Box,
  Button,
  IconButton
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";



 function ImportX3UsersForm({
  // data
  folders = [],
  groups = [],
  users = [],

  // state
  value,

  // ui
  loadingInit = false,
  loadingUsers = false,
  saving = false,

  // labels
  folderLabel = "Folder",
  addUsersLabel = "Add Users",

  // events
  onChange,
  onToggleUser,
  onToggleSelectAll,
  onX3FolderRefresh,
  onSubmit,
}) {
  const selectedCount = value?.selectedUsrSet?.size ?? 0;

  const allKeys = useMemo(() => users.map((u) => u.USR).filter(Boolean), [users]);
  const allSelected = allKeys.length > 0 && selectedCount === allKeys.length;

  const filteredUsers = useMemo(() => {
    const q = (value?.userSearch ?? "").trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const usr = (u.USR ?? "").toLowerCase();
      const name = (u.INTUSR ?? "").toLowerCase();
      const email = (u.ADDEML ?? "").toLowerCase();
      return usr.includes(q) || name.includes(q) || email.includes(q);
    });
  }, [users, value?.userSearch]);

  return (
    <Box sx={{ position: "relative" }}>
      <Stack spacing={2}>
        {/* Folder */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 260 }} disabled={loadingInit || saving}>
          <InputLabel>{folderLabel}</InputLabel>
          <Select
            label={folderLabel}
            value={value.folder}
            onChange={(e) => onChange({ folder: e.target.value })}
          >
            {folders.map((f) => (
              <MenuItem key={f} value={f}>{f}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          onClick={onX3FolderRefresh}
          disabled={loadingInit || saving}
          size="small"
          title="Reload folders"
        >
        <RefreshIcon />
        </IconButton>

        {(loadingInit || loadingUsers) && <CircularProgress size={22} />}

        <Typography variant="body2" color="text.secondary">
          {value.folder ? `Loaded: ${users.length} users` : "Pick a folder to load users"}
        </Typography>
      </Stack>

      <Divider />

      {/* Users */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          X3 Users
        </Typography>

        <TextField
          size="small"
          placeholder="Search users…"
          value={value.userSearch}
          onChange={(e) => onChange({ userSearch: e.target.value })}
          disabled={loadingUsers || saving}
          sx={{ width: 320 }}
          InputProps={{
            endAdornment: value.userSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onChange({userSearch: ""})}
                  edge="end"
                  title="Clear"
                >
                  <ClearIcon fontSize="small"/>
                </IconButton>
              </InputAdornment>
            ) :null
          }}
        />

        <Button
          onClick={onToggleSelectAll}
          disabled={!users.length || loadingUsers || saving}
        >
          {allSelected ? "Unselect all" : "Select all"}
        </Button>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          maxHeight: 420,
          overflow: "auto",
          position: "relative",
          minHeight: 200,
        }}
      >
        <List dense disablePadding>
          {filteredUsers.map((u) => {
            const checked = value.selectedUsrSet?.has(u.USR);
            return (
              <ListItemButton
                key={u.USR}
                onClick={() => onToggleUser(u.USR)}
                selected={!!checked}
                sx={{ py: 1 }}
                disabled={loadingUsers || saving}
              >
                <ListItemIcon>
                  <Checkbox edge="start" checked={!!checked} tabIndex={-1} disabled={loadingUsers || saving} />
                </ListItemIcon>
                <ListItemText
                  primary={`${u.USR ?? ""} — ${u.INTUSR ?? ""}`}
                  secondary={[
                    u.ADDEML ? `Email: ${u.ADDEML}` : null,
                    u.$etag ? `Last Login: ${u.$etag}` : null,
                  ].filter(Boolean).join(" • ")}
                />
              </ListItemButton>
            );
          })}

          {!loadingUsers && value.folder && filteredUsers.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No users match your search.
              </Typography>
            </Box>
          )}

          {!loadingUsers && !value.folder && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a folder to load users.
              </Typography>
            </Box>
          )}
        </List>

        {/* Loading overlay for user list */}
        {loadingUsers && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Loading users from {value.folder}...
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary">
        Selected: {selectedCount}
      </Typography>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Imported users will be required to set their own password on first login.
        </Typography>
      </Stack>

      <Divider />

      {/* Groups */}
      <Stack spacing={1}>
        <Typography variant="h6">Groups</Typography>

        <Autocomplete
          multiple
          options={groups}
          getOptionLabel={(opt) => opt?.name || ""}
          value={groups.filter((g) => value.selectedGroups.includes(g.name))}
          onChange={(_, selected) => onChange({ selectedGroups: (selected || []).map((g) => g.name) })}
          disabled={loadingInit || loadingUsers || saving}
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              label="Groups"
              placeholder="Select groups"
              size="small"
            />
          )}
          sx={{ maxWidth: 520 }}
        />
      </Stack>

      <Divider />

      {/* Submit */}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loadingInit || loadingUsers || saving || selectedCount === 0}
          startIcon={saving ? <CircularProgress size={22} color="inherit" /> : null}
          sx={{ textTransform: 'none' }}
        >
          {saving ? "Saving..." : addUsersLabel}
        </Button>
      </Stack>
      </Stack>

      {/* Loading overlay for initial data */}
      {loadingInit && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 10,
            borderRadius: 1,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading folders and groups...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
export default ImportX3UsersForm;